import { auth } from "../../configs/firebaseConfig";

const AUTH_SETTLE_TIMEOUT_MS = 2000;

export async function waitForAuthInitialization() {
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }
}

export function getPreferredAuthUser(authUser, contextUser, expectedUid = "") {
  const users = [authUser, contextUser].filter(Boolean);

  if (expectedUid) {
    const matchingUsers = users.filter((user) => user.uid === expectedUid);
    return (
      matchingUsers.find((user) => user.isAnonymous !== true) ||
      matchingUsers[0] ||
      null
    );
  }

  if (
    authUser?.uid &&
    contextUser?.uid &&
    authUser.uid !== contextUser.uid
  ) {
    return null;
  }

  if (authUser?.uid && authUser.uid === contextUser?.uid) {
    return authUser.isAnonymous !== true ? authUser : contextUser;
  }

  return authUser || contextUser || null;
}

function matchesSettledUser(user, expectedUid, requirePermanentUser) {
  if (!user || (expectedUid && user.uid !== expectedUid)) return false;
  return !requirePermanentUser || user.isAnonymous !== true;
}

export function isSameAuthUser(firstUser, secondUser) {
  return Boolean(firstUser?.uid && firstUser.uid === secondUser?.uid);
}

export async function waitForSettledAuthUser({
  contextUser = null,
  expectedUid = "",
  requirePermanentUser = false,
  skipInitialization = false,
} = {}) {
  if (!skipInitialization) {
    await waitForAuthInitialization();
  }

  const getCurrentUser = () =>
    getPreferredAuthUser(auth.currentUser, contextUser, expectedUid);
  const immediateUser = getCurrentUser();

  if (matchesSettledUser(immediateUser, expectedUid, requirePermanentUser)) {
    return immediateUser;
  }

  if (typeof auth.onAuthStateChanged !== "function") {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      await Promise.resolve();
      const microtaskUser = getCurrentUser();
      if (matchesSettledUser(microtaskUser, expectedUid, requirePermanentUser)) {
        return microtaskUser;
      }
    }
    const fallbackUser = getCurrentUser();
    return matchesSettledUser(fallbackUser, expectedUid, requirePermanentUser)
      ? fallbackUser
      : null;
  }

  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};
    let timeoutId = null;

    const finish = (user) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    };

    const listenerUnsubscribe = auth.onAuthStateChanged((user) => {
      const settledUser = getPreferredAuthUser(user, contextUser, expectedUid);

      if (matchesSettledUser(settledUser, expectedUid, requirePermanentUser)) {
        finish(settledUser);
      }
    });
    unsubscribe = listenerUnsubscribe || (() => {});
    if (settled) unsubscribe();

    if (settled) return;

    timeoutId = setTimeout(() => {
      const fallbackUser = getCurrentUser();
      finish(
        matchesSettledUser(fallbackUser, expectedUid, requirePermanentUser)
          ? fallbackUser
          : null
      );
    }, AUTH_SETTLE_TIMEOUT_MS);
  });
}
